import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 33. CUSTOM FIELDS
// Un professionnel peut ajouter ses propres champs
// (« Numéro de dossier interne », « Type de projet », « Préférence client »…)
// sans modifier la structure principale de la base.
//   Entity → Custom Field Definition → Custom Field Value
// Types validés : text · number · date · boolean · select · multi-select · relation
// ─────────────────────────────────────────────────────────────

export type CustomFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multi-select' | 'relation';

export const CUSTOM_FIELD_TYPES: { key: CustomFieldType; label: string; icon: string }[] = [
  { key: 'text', label: 'Texte', icon: 'ri-text' },
  { key: 'number', label: 'Nombre', icon: 'ri-hashtag' },
  { key: 'date', label: 'Date', icon: 'ri-calendar-line' },
  { key: 'boolean', label: 'Booléen', icon: 'ri-toggle-line' },
  { key: 'select', label: 'Liste', icon: 'ri-list-check' },
  { key: 'multi-select', label: 'Liste multiple', icon: 'ri-list-check-2' },
  { key: 'relation', label: 'Relation', icon: 'ri-links-line' },
];

export function customFieldTypeMeta(key: string | null | undefined) {
  return CUSTOM_FIELD_TYPES.find((t) => t.key === key) || CUSTOM_FIELD_TYPES[0];
}

/** Entités auxquelles des champs personnalisés peuvent être rattachés. */
export const CUSTOM_FIELD_ENTITIES: { key: string; label: string; icon: string }[] = [
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line' },
  { key: 'contact', label: 'Contact', icon: 'ri-contacts-line' },
  { key: 'product', label: 'Produit', icon: 'ri-shopping-bag-line' },
  { key: 'service', label: 'Service', icon: 'ri-service-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line' },
  { key: 'project', label: 'Projet', icon: 'ri-folders-line' },
  { key: 'reservation', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'professional', label: 'Professionnel', icon: 'ri-user-2-line' },
];

export function customFieldEntityMeta(key: string | null | undefined) {
  return CUSTOM_FIELD_ENTITIES.find((e) => e.key === key) || null;
}

/** Entités cibles possibles pour un champ de type « relation ». */
export const RELATION_TARGET_ENTITIES: string[] = [
  'customer', 'contact', 'product', 'service', 'order', 'project', 'reservation', 'professional', 'business',
];

export interface CoreCustomField {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  entity_type: string;
  field_key: string;
  label: string;
  field_type: string;
  options: unknown;
  relation_entity: string | null;
  required: boolean | null;
  sort_order: number | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CoreCustomFieldValue {
  id: number;
  field_id: number;
  organization_id: number | null;
  entity_type: string;
  entity_id: string;
  value: unknown;
  created_at: string | null;
  updated_at: string | null;
}

function slugField(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `field_${Date.now()}`;
}

export async function listCustomFields(organizationId: number, entityType?: string): Promise<CoreCustomField[]> {
  let query = supabase
    .from('core_custom_fields')
    .select('*')
    .eq('organization_id', organizationId)
    .order('sort_order', { ascending: true });
  if (entityType) query = query.eq('entity_type', entityType);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreCustomField[];
}

export interface CustomFieldInput {
  tenantId: number | null;
  organizationId: number;
  entityType: string;
  label: string;
  fieldType: CustomFieldType;
  options?: string[];
  relationEntity?: string | null;
  required?: boolean;
}

export async function createCustomField(input: CustomFieldInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_custom_fields')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      entity_type: input.entityType,
      field_key: slugField(input.label),
      label: input.label,
      field_type: input.fieldType,
      options: input.options || [],
      relation_entity: input.relationEntity || null,
      required: !!input.required,
      sort_order: 0,
      is_active: true,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCustomField(
  id: number,
  patch: { label?: string; fieldType?: CustomFieldType; options?: string[]; relationEntity?: string | null; required?: boolean; isActive?: boolean; sortOrder?: number },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.label !== undefined) { payload.label = patch.label; payload.field_key = slugField(patch.label); }
  if (patch.fieldType !== undefined) payload.field_type = patch.fieldType;
  if (patch.options !== undefined) payload.options = patch.options;
  if (patch.relationEntity !== undefined) payload.relation_entity = patch.relationEntity || null;
  if (patch.required !== undefined) payload.required = patch.required;
  if (patch.isActive !== undefined) payload.is_active = patch.isActive;
  if (patch.sortOrder !== undefined) payload.sort_order = patch.sortOrder;
  const { error } = await supabase.from('core_custom_fields').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCustomField(id: number): Promise<void> {
  await supabase.from('core_custom_field_values').delete().eq('field_id', id);
  const { error } = await supabase.from('core_custom_fields').delete().eq('id', id);
  if (error) throw error;
}

export async function listCustomFieldValues(
  entityType: string,
  entityId: string | number,
): Promise<CoreCustomFieldValue[]> {
  const { data, error } = await supabase
    .from('core_custom_field_values')
    .select('*')
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId));
  if (error) throw error;
  return (data || []) as CoreCustomFieldValue[];
}

export async function setCustomFieldValue(input: {
  fieldId: number;
  organizationId: number;
  entityType: string;
  entityId: string | number;
  value: unknown;
}): Promise<void> {
  const { data: existing } = await supabase
    .from('core_custom_field_values')
    .select('id')
    .eq('field_id', input.fieldId)
    .eq('entity_type', input.entityType)
    .eq('entity_id', String(input.entityId))
    .maybeSingle();
  if (existing?.id) {
    const { error } = await supabase
      .from('core_custom_field_values')
      .update({ value: input.value, updated_at: new Date().toISOString() })
      .eq('id', existing.id as number);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_custom_field_values').insert({
      field_id: input.fieldId,
      organization_id: input.organizationId,
      entity_type: input.entityType,
      entity_id: String(input.entityId),
      value: input.value,
    });
    if (error) throw error;
  }
}

/** Comptage des valeurs saisies par champ (pour l'aperçu admin). */
export async function fetchCustomFieldUsage(organizationId: number): Promise<Record<number, number>> {
  const map: Record<number, number> = {};
  const { data } = await supabase
    .from('core_custom_field_values')
    .select('field_id')
    .eq('organization_id', organizationId);
  (data || []).forEach((r) => {
    const id = Number((r as { field_id: number }).field_id);
    map[id] = (map[id] || 0) + 1;
  });
  return map;
}